/**
 * NGR report wizard (Phase 3) — the Saved-Views authoring vocabulary (R14)
 * over Cube metadata: pick a dataset, pick fields, scope workspaces, preview,
 * save. Authoring is org_admin-only (R15); the page gates rendering.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { reportGridLightTheme } from '../shared/ag-grid-theme';
import Button from '@shared/ui/Button';
import { cubeColumnDefs } from './cubeGrid';
import { SpinnerIcon, SuccessIcon, WarningIcon } from '../icons';
import {
  fetchNgrMeta,
  executeNgrQuery,
  createNgrReport,
  updateNgrReport,
  NgrCubeMeta,
  NgrReportConfig,
  NgrReportDefinition,
} from '../../services/ngrReportsService';

const STEPS = ['Dataset', 'Fields', 'Workspaces', 'Preview', 'Save'] as const;

interface Props {
  organizationId: string;
  workspaces: { id: string; name: string }[];
  /** When present, the wizard edits this definition (prefilled, saves via PUT). */
  editing?: NgrReportDefinition | null;
  onClose: () => void;
  onSaved: () => void;
}

export const NgrReportWizard: React.FC<Props> = ({ organizationId, workspaces, editing, onClose, onSaved }) => {
  const [step, setStep] = useState(0);
  const [cubeName, setCubeName] = useState<string | null>(() => {
    const member = editing?.config.query.measures?.[0] || editing?.config.query.dimensions?.[0];
    return member ? member.split('.')[0] : null;
  });
  const [measures, setMeasures] = useState<string[]>(editing?.config.query.measures || []);
  const [dimensions, setDimensions] = useState<string[]>(editing?.config.query.dimensions || []);
  const [workspaceIds, setWorkspaceIds] = useState<string[] | 'all'>(
    editing?.config.scope.workspaceIds ?? 'all'
  );
  const [name, setName] = useState(editing?.name || '');
  const [description, setDescription] = useState(editing?.description || '');
  // Branding: empty object = inherit the org's Report Branding (Org Settings).
  // A populated override merges over the org defaults at render time — the
  // same override-then-fallback rule Outreach uses.
  const initialBranding = (editing?.config.branding || {}) as {
    show_organization_logo?: boolean;
    logo_position?: 'left' | 'center' | 'right';
    primary_color?: string;
  };
  const [useOrgBranding, setUseOrgBranding] = useState(Object.keys(initialBranding).length === 0);
  const [brandLogo, setBrandLogo] = useState(initialBranding.show_organization_logo ?? true);
  const [brandLogoPosition, setBrandLogoPosition] = useState<'left' | 'center' | 'right'>(
    initialBranding.logo_position || 'left'
  );
  const [brandPrimary, setBrandPrimary] = useState(initialBranding.primary_color || '#2563eb');
  const queryClient = useQueryClient();

  const { data: cubes, isLoading: metaLoading } = useQuery({
    queryKey: ['ngr-meta', organizationId],
    queryFn: () => fetchNgrMeta(organizationId),
    staleTime: 5 * 60 * 1000,
  });

  const selectedCube: NgrCubeMeta | undefined = useMemo(
    () => cubes?.find((c) => c.name === cubeName),
    [cubes, cubeName]
  );

  const query = useMemo(
    () => ({ measures, dimensions, limit: 100 }),
    [measures, dimensions]
  );

  const preview = useQuery({
    queryKey: ['ngr-preview', organizationId, query],
    queryFn: () => executeNgrQuery(organizationId, query),
    enabled: step === 3 && (measures.length > 0 || dimensions.length > 0),
  });

  const save = useMutation({
    mutationFn: () => {
      const config: NgrReportConfig = {
        ...(editing?.config || { layout: 'table' as const, charts: [] }),
        query,
        scope: { workspaceIds },
        branding: useOrgBranding
          ? {}
          : {
              show_organization_logo: brandLogo,
              logo_position: brandLogoPosition,
              primary_color: brandPrimary,
            },
        // New reports start author-private (R15); edits keep their availability.
        availability: editing ? editing.config.availability : null,
      };
      return editing
        ? updateNgrReport(organizationId, editing.id, { name, description, config })
        : createNgrReport(organizationId, { name, description, config });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ngr-reports', organizationId] });
      onSaved();
    },
  });

  const toggle = (list: string[], setList: (v: string[]) => void, member: string) =>
    setList(list.includes(member) ? list.filter((m) => m !== member) : [...list, member]);

  const canNext =
    (step === 0 && !!cubeName) ||
    (step === 1 && measures.length + dimensions.length > 0) ||
    step === 2 ||
    step === 3 ||
    (step === 4 && name.trim().length > 0);

  const columnDefs = useMemo(() => cubeColumnDefs(preview.data?.rows || []), [preview.data]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
      <div className="w-full max-w-3xl rounded-lg bg-white dark:bg-background-dark-secondary p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">New report</h2>
          <div className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
            Step {step + 1} of {STEPS.length}: {STEPS[step]}
          </div>
        </div>

        {step === 0 && (
          <div className="grid grid-cols-2 gap-2">
            {metaLoading && <SpinnerIcon className="w-5 h-5 animate-spin" />}
            {cubes?.map((cube) => (
              <button
                key={cube.name}
                type="button"
                onClick={() => setCubeName(cube.name)}
                className={`rounded border p-3 text-left text-sm ${
                  cubeName === cube.name ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-border-light dark:border-border-dark hover:border-border-light dark:border-border-dark'
                }`}
              >
                <div className="font-medium">{cube.title || cube.name}</div>
                {cube.description && <div className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">{cube.description}</div>}
              </button>
            ))}
          </div>
        )}

        {step === 1 && selectedCube && (
          <div className="grid max-h-80 grid-cols-2 gap-4 overflow-y-auto">
            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-text-tertiary dark:text-text-dark-tertiary">Measures</div>
              {selectedCube.measures.map((m) => (
                <label key={m.name} className="flex items-center gap-2 py-1 text-sm">
                  <input
                    type="checkbox"
                    checked={measures.includes(m.name)}
                    onChange={() => toggle(measures, setMeasures, m.name)}
                  />
                  {m.shortTitle || m.title}
                </label>
              ))}
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-text-tertiary dark:text-text-dark-tertiary">Dimensions</div>
              {selectedCube.dimensions.map((d) => (
                <label key={d.name} className="flex items-center gap-2 py-1 text-sm">
                  <input
                    type="checkbox"
                    checked={dimensions.includes(d.name)}
                    onChange={() => toggle(dimensions, setDimensions, d.name)}
                  />
                  {d.shortTitle || d.title}
                </label>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={workspaceIds === 'all'} onChange={() => setWorkspaceIds('all')} />
              All my workspaces (new workspaces flow in automatically)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={workspaceIds !== 'all'}
                onChange={() => setWorkspaceIds([])}
              />
              Selected workspaces
            </label>
            {workspaceIds !== 'all' &&
              workspaces.map((ws) => (
                <label key={ws.id} className="ml-6 flex items-center gap-2 py-0.5 text-sm">
                  <input
                    type="checkbox"
                    checked={workspaceIds.includes(ws.id)}
                    onChange={() =>
                      setWorkspaceIds(
                        workspaceIds.includes(ws.id)
                          ? workspaceIds.filter((id) => id !== ws.id)
                          : [...workspaceIds, ws.id]
                      )
                    }
                  />
                  {ws.name}
                </label>
              ))}
          </div>
        )}

        {step === 3 && (
          <div className="h-80">
            {preview.isLoading && (
              <div className="flex h-full items-center justify-center text-sm text-text-tertiary dark:text-text-dark-tertiary">
                <SpinnerIcon className="mr-2 w-4 h-4 animate-spin" /> Running preview…
              </div>
            )}
            {preview.isError && (
              <div className="flex h-full items-center justify-center text-sm text-red-600">
                <WarningIcon className="mr-2 w-4 h-4" /> Preview failed — check your field selection.
              </div>
            )}
            {preview.data && (
              <AgGridReact
                theme={reportGridLightTheme}
                rowData={preview.data.rows}
                columnDefs={columnDefs}
                defaultColDef={{ sortable: true, resizable: true, flex: 1, minWidth: 120 }}
              />
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            <input
              className="input"
              placeholder="Report name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <textarea
              className="input"
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="rounded border border-border-light dark:border-border-dark p-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={useOrgBranding}
                  onChange={(e) => setUseOrgBranding(e.target.checked)}
                />
                Use organization Report Branding (set under Org Settings)
              </label>
              {!useOrgBranding && (
                <div className="mt-3 space-y-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={brandLogo} onChange={(e) => setBrandLogo(e.target.checked)} />
                    Show organization logo
                  </label>
                  <label className="flex items-center gap-2">
                    Logo position
                    <select
                      className="input w-auto"
                      value={brandLogoPosition}
                      onChange={(e) => setBrandLogoPosition(e.target.value as 'left' | 'center' | 'right')}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2">
                    Primary color
                    <input
                      type="color"
                      value={brandPrimary}
                      onChange={(e) => setBrandPrimary(e.target.value)}
                      aria-label="Primary color"
                    />
                  </label>
                </div>
              )}
            </div>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Saved reports are private to you until you make them available to workspaces.
            </p>
            {save.isError && (
              <p className="text-sm text-red-600">Save failed — try again.</p>
            )}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                Back
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button variant="primary" disabled={!canNext} onClick={() => setStep(step + 1)}>
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                icon={SuccessIcon}
                loading={save.isPending}
                disabled={!canNext}
                onClick={() => save.mutate()}
              >
                Save report
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
